/* eslint semi:0 */
/**
 * Script loader, powered by configuration payloads. All attributes can
 * be literals, functions or promises.
 */
import { noop } from './fn'
import { queryString } from './window'
import { info, warn } from '../application/console'
import { create as makeEmptyObj } from './object'
import { createElement, nodeByTagName } from './dom'
import {
  externalScriptsConfig,
  SCRIPT_LOADER_CONF_DEFAULTS,
} from '../organizedByType/config/externalScripts'
import { registerUtils, registerInNamespace } from '../application/nm'

const { noThirdParty } = queryString

let firstScriptNode = null
let firstScriptParentNode = null

function performQueuedCallbacks(callbackQueue) {
  while (callbackQueue.length) {
    callbackQueue.shift().call()
  }
  return callbackQueue
}

function debugMessage(...args) {
  if (queryString.debugExternals) {
    info('[scriptLoader]', ...args)
  }
}

const DONT_LOAD_SCRIPT_VALUE = false

function insertScriptBeforeFirstScript(scriptTag) {
  if (!firstScriptNode) {
    firstScriptNode = nodeByTagName('script')
    firstScriptParentNode = firstScriptNode.parentNode
  }
  firstScriptParentNode.insertBefore(scriptTag, firstScriptNode)
}

function loadExternalScript(config) {
  const callbackQueue = []
  let isLoaded = false
  let isLoading = false

  return function scriptLoader(callback) {
    // eslint-disable-line complexity
    if (callback instanceof Function) {
      callbackQueue.push(callback)
    }
    debugMessage(config, { callback, callbackQueue, isLoading, isLoaded })
    if (isLoaded) {
      return performQueuedCallbacks(callbackQueue)
    }
    if (!isLoading) {
      isLoading = true
      const scriptConf = Object.assign(makeEmptyObj(), SCRIPT_LOADER_CONF_DEFAULTS, config)
      const scriptOnLoad = function scriptOnLoadHandler() {
        scriptConf.onload()
        isLoaded = true
        performQueuedCallbacks(callbackQueue)
      }
      const { beforeLoad } = scriptConf
      delete scriptConf.beforeLoad
      if (beforeLoad() === DONT_LOAD_SCRIPT_VALUE) {
        debugMessage(
          `skipping ${config.name} because`,
          config.beforeLoad,
          'returned',
          DONT_LOAD_SCRIPT_VALUE,
          config,
          { callback, callbackQueue, isLoading, isLoaded }
        )
        scriptOnLoad()
        return callbackQueue
      }
      const scriptTag = createElement('script')
      const configurableAttrs = Object.keys(scriptConf).filter(
        attr => attr !== 'onload' && scriptConf[attr] !== undefined
      )
      const promisesToWaitFor = []
      for (const attr of configurableAttrs) {
        const scriptAttributeValue = scriptConf[attr]
        if (scriptAttributeValue instanceof Function) {
          const scriptAttributeFunctionValue = scriptAttributeValue()
          if (scriptAttributeFunctionValue instanceof Promise) {
            promisesToWaitFor.push(scriptAttributeFunctionValue)
            scriptAttributeFunctionValue
              .then(scriptTag.setAttribute.bind(scriptTag, attr))
              .catch(warn)
          }
          scriptTag.setAttribute(attr, scriptAttributeFunctionValue)
        } else {
          scriptTag.setAttribute(attr, scriptAttributeValue)
        }
      }
      scriptTag.onload = scriptOnLoad
      scriptTag.onerror =
        scriptTag.onerror ||
        function onScriptError(e) {
          warn(`Error loading script '${config.name}' from '${config.src}'!`, e, this)
        }
      if (promisesToWaitFor.length) {
        Promise.all(promisesToWaitFor)
          .then(_ => insertScriptBeforeFirstScript(scriptTag))
          .catch(warn)
      } else {
        insertScriptBeforeFirstScript(scriptTag)
      }
      return callbackQueue
    }
    return callbackQueue
  }
}

const loadedScripts = makeEmptyObj()

export function load(options) {
  if (noThirdParty) {
    return noop
  }
  let { config, loadNow } = options
  const name = (config && config.name) || options.name // eslint-disable-line no-shadow
  let possiblyLoadedScript = loadedScripts[name]
  if (!possiblyLoadedScript) {
    config = config || externalScriptsConfig[name] || {}
    const { src } = config
    if (!src) {
      const missingConfOrSrcErrorMessage = `Missing src '${src}' in config for loading external script '${name}'!`
      warn(missingConfOrSrcErrorMessage, { name, config })
      throw new Error(missingConfOrSrcErrorMessage)
    }
    possiblyLoadedScript = loadedScripts[name] = loadExternalScript(config)
    if (loadNow) {
      possiblyLoadedScript()
    }
  }
  return possiblyLoadedScript
}

export function loadAll(...scriptConfs) {
  // TODO: investigate why when converted to an arrow function,
  // it broke here when called via
  // script.external.loadAll.apply(null, [...scriptConfigs])
  return scriptConfs.map(load)
}

/* eslint-disable complexity */
export const external = {
  load,
  loadAll,
}

const script = Object.assign(makeEmptyObj(), {
  external,
})

registerUtils({ script })
registerInNamespace({ loadedScripts })



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/utils/script.js